import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAtomValue } from "jotai";
import { groupBy } from "lodash";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import CheckIcon from "@mui/icons-material/Check";
import { Section } from "../components";
import { dayToStringShort, dateToShortTime, useCurrentDate } from "../lib";
import { LoadingDots } from "../LoadingDots";
import { Z_INDEXES } from "../constants";
import {
  bookSession,
  listBookedSessions,
  listUpcomingFreestyleSessions,
  PaymentMethod,
  recordRecentBooking,
  Session,
  useSelectedRecusMemberId,
} from "./api";
import { recusAuthStateAtom } from "../vendor";
import { RecusBookButton } from "./BookButton";
import penguinMoney from "../penguin_money.gif";

const SUCCESS_DISPLAY_MS = 5000;

export function ScheduleRecus() {
  const authState = useAtomValue(recusAuthStateAtom);
  const [memberId] = useSelectedRecusMemberId();
  const queryClient = useQueryClient();
  const {
    data: sessions,
    status,
    isFetching,
  } = useQuery(
    ["upcomingSessions", "recus", authState?.userId],
    () => listUpcomingFreestyleSessions(authState!),
    { enabled: !!authState }
  );
  const { data: bookedData, isFetching: bookedIsFetching } = useQuery(
    ["bookedSessions", "recus", memberId],
    () => listBookedSessions(authState!, memberId!),
    { enabled: !!authState && !!memberId, refetchOnWindowFocus: true }
  );

  const TO_SHOW = 6;
  const [showNum, setShowNum] = useState(TO_SHOW);
  const [expandedSessions, setExpandedSessions] = useState<Session[] | null>(
    null
  );
  const slots = sessions ?? [];
  const bookedIds = new Set((bookedData ?? []).map((b) => b.sessionId));

  const [justBooked, setJustBooked] = useState(false);
  const successTimer = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (successTimer.current) window.clearTimeout(successTimer.current);
    };
  }, []);

  const bookMutation = useMutation({
    mutationFn: async (args: { session: Session; pm: PaymentMethod }) => {
      if (!authState || !memberId) throw new Error("missing auth or member");
      return bookSession(
        authState,
        args.session.id,
        args.pm.stripePaymentMethodId,
        memberId
      );
    },
    onSuccess: (result, args) => {
      recordRecentBooking(args.session.startsAt);
      queryClient.setQueryData(
        ["bookedSessions", "recus", memberId],
        result.bookedSessions
      );
      queryClient.invalidateQueries(["upcomingSessions"]);
      setJustBooked(true);
      if (successTimer.current) window.clearTimeout(successTimer.current);
      successTimer.current = window.setTimeout(() => {
        setJustBooked(false);
        setExpandedSessions(null);
      }, SUCCESS_DISPLAY_MS);
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  const expandedDate = expandedSessions?.[0]?.startsAt;
  const headerString = expandedDate
    ? `← viewing ${dayToStringShort[expandedDate.getDay()]} ${expandedDate.getDate()}`
    : "upcoming schedule";

  const header = (
    <h2
      className="text-text-secondary w-full text-lg mb-4 cursor-pointer"
      onClick={() => setExpandedSessions(null)}
    >
      {headerString}
      {(isFetching || bookedIsFetching) && <LoadingDots />}
    </h2>
  );

  if (bookMutation.isLoading) {
    return (
      <Section>
        {header}
        <div className="flex flex-col items-center py-6">
          <img
            className="w-20 h-20 opacity-80 rounded"
            src={penguinMoney}
            alt="booking"
          />
        </div>
      </Section>
    );
  }

  if (justBooked) {
    return (
      <Section>
        {header}
        <div className="flex flex-col items-center py-6 gap-3">
          <div className="text-lg">successfully booked! 🎉</div>
        </div>
      </Section>
    );
  }

  if (status === "error") {
    return (
      <Section>
        {header}
        <div
          role="alert"
          className="w-full rounded-md border border-red-200 bg-red-100 p-5 text-center text-red-900"
        >
          couldn't load schedule, please try again later!
        </div>
      </Section>
    );
  }

  if (!authState || status !== "success") {
    return (
      <Section>
        {header}
        <div>loading...</div>
      </Section>
    );
  }

  if (expandedSessions) {
    return (
      <Section>
        {header}
        <DaySlotSingle
          sessions={expandedSessions}
          bookedIds={bookedIds}
          onBook={(session, pm) => bookMutation.mutate({ session, pm })}
        />
      </Section>
    );
  }

  const slotsByDay = groupBy(slots, (s) => dayKey(s.startsAt));
  const dayKeys = Object.keys(slotsByDay);

  return (
    <Section>
      {header}
      <div>
        {dayKeys.length === 0 && <div>No upcoming freestyle sessions.</div>}
        <div className="w-full grid grid-cols-2 gap-4 max-w-sm">
          {dayKeys.slice(0, showNum).map((k) => (
            <DayCard
              key={k}
              sessions={slotsByDay[k]}
              bookedIds={bookedIds}
              onClick={() => setExpandedSessions(slotsByDay[k])}
            />
          ))}
        </div>
        {showNum < dayKeys.length && (
          <div className="flex flex-row justify-center">
            <button
              className="mt-5 text-text-secondary text-sm underline decoration-dotted"
              onClick={() => setShowNum((n) => n + TO_SHOW)}
            >
              show more
            </button>
          </div>
        )}
      </div>
    </Section>
  );
}

function dayKey(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toDateString();
}

function DaySlotSingle(props: {
  sessions: Session[];
  bookedIds: Set<string>;
  onBook: (session: Session, pm: PaymentMethod) => void;
}) {
  const now = useCurrentDate(60_000);
  return (
    <div className="bg-bg-brand-alt text-text-on-brand-alt p-6 w-full rounded-md">
      <div
        className="text-xl justify-center items-center"
        style={{
          display: "grid",
          gridTemplateColumns: "4fr 2fr 2fr",
          rowGap: "20px",
          columnGap: "4px",
        }}
      >
        {props.sessions.map((s) => {
          const durationMins = Math.round(
            (s.endsAt.getTime() - s.startsAt.getTime()) / 60000
          );
          const totalEnrollment = s.participantCount + s.pendingParticipantCount;
          const isBooked = props.bookedIds.has(s.id);
          const isPast = s.startsAt < now;
          return (
            <div className="contents" key={s.id}>
              <span className="col-start-1 text-2xl relative">
                {isBooked && (
                  <span className="absolute -left-5 top-1/2 -translate-y-1/2">
                    <CheckIcon color="success" fontSize="small" />
                  </span>
                )}
                <RecusBookButton
                  session={s}
                  smaller
                  inactive={isBooked || isPast}
                  onBook={props.onBook}
                />
              </span>
              <span className="col-start-2">{durationMins}min</span>
              <span className="col-start-3 justify-self-end">
                {totalEnrollment}⛸️
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayCard(props: {
  sessions: Session[];
  bookedIds: Set<string>;
  onClick: () => void;
}) {
  const date = props.sessions[0].startsAt;
  const dateStr = `${dayToStringShort[date.getDay()]} ${date.getDate()}`;
  return (
    <div className="relative">
      <div
        className={`h-full bg-bg-brand-alt text-text-on-brand-alt p-5 relative rounded-md cursor-pointer ${Z_INDEXES.DAY_SLOT}`}
        onClick={props.onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.onClick();
          }
        }}
      >
        <div className="text-lg xs:text-xl font-normal">{dateStr}</div>
        {props.sessions.map((s) => {
          const totalEnrollment =
            s.participantCount + s.pendingParticipantCount;
          const isBooked = props.bookedIds.has(s.id);
          return (
            <div
              key={s.id}
              className="text-base xs:text-lg text-justify flex flex-row"
            >
              <span className="flex-auto flex flex-row items-center mr-4">
                {isBooked && (
                  <div className="absolute -ml-[18px] -mt-[3px]">
                    <CheckIcon color="success" fontSize="inherit" />
                  </div>
                )}
                {dateToShortTime(s.startsAt)}
              </span>
              <span className="flex-auto flex flex-row-reverse">
                {totalEnrollment}⛸️
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
